// ABOUTME: Renders a variant pricing engine failure with its code meaning, the failing rule's
// ABOUTME: location, and the structured diagnostics that pinpoint why the line could not be priced.

import { Alert, Box, Chip, Stack, Typography } from '@ds/coolshop';
import { useIntl } from 'react-intl';

import { ExplainError, ExplainErrorDiagnostics, VariantPricingErrorCode } from './useExplainPrice';

// Human-readable meaning for each engine error code. This copy is seller-facing debug guidance, so
// it lives in the UI (translated through react-intl) rather than being echoed from the server.
const CODE_MEANINGS: Record<VariantPricingErrorCode, { id: string; defaultMessage: string }> = {
    MISSING_PRICE_KEY: {
        id: 'cpq.pricing-trace.error.MISSING_PRICE_KEY',
        defaultMessage:
            'The rule produced a price key that has no entry in the price book, so this line cannot be priced. A key like "0" usually means the key expression evaluated to a number instead of a real price code — check the rule’s key expression and the selection it depends on.',
    },
    MODEL_HAS_NO_PRICES: {
        id: 'cpq.pricing-trace.error.MODEL_HAS_NO_PRICES',
        defaultMessage:
            'The model has no price records: it was not in the VA00 export, or its id is spelled differently from the price book. Re-run the import or check the model id.',
    },
    MISSING_TABLE_ROW: {
        id: 'cpq.pricing-trace.error.MISSING_TABLE_ROW',
        defaultMessage:
            'A decision-table lookup found no row matching the given keys. Check the lookup values against the table’s rows.',
    },
    MISSING_TABLE: {
        id: 'cpq.pricing-trace.error.MISSING_TABLE',
        defaultMessage:
            'The decision table referenced by the rule does not exist. Check the table reference.',
    },
    EXPRESSION_ERROR: {
        id: 'cpq.pricing-trace.error.EXPRESSION_ERROR',
        defaultMessage:
            'A rule expression failed to evaluate to the expected type (e.g. a factor that is not a number). Check the expression.',
    },
    INVALID_RULE: {
        id: 'cpq.pricing-trace.error.INVALID_RULE',
        defaultMessage:
            'The rule is malformed — it is missing a required target, key, or table reference. Fix the rule definition.',
    },
    FACTOR_TARGET_NOT_FOUND: {
        id: 'cpq.pricing-trace.error.FACTOR_TARGET_NOT_FOUND',
        defaultMessage:
            'A factor rule targeted a price key that was never emitted, so there is nothing to multiply. Check the emit rule the factor depends on.',
    },
    MODEL_NOT_ASSIGNED: {
        id: 'cpq.pricing-trace.error.MODEL_NOT_ASSIGNED',
        defaultMessage:
            'The configuration node has no pricing model assigned, so no procedures could run for it.',
    },
    MISSING_PROCEDURE: {
        id: 'cpq.pricing-trace.error.MISSING_PROCEDURE',
        defaultMessage:
            'The pricing procedure referenced by the model was not found. Check the procedure code.',
    },
    CURRENCY_MISMATCH: {
        id: 'cpq.pricing-trace.error.CURRENCY_MISMATCH',
        defaultMessage:
            'The line mixes price keys booked in different currencies, which would produce a meaningless total. Align the price book currencies.',
    },
};

// Diagnostic fields that pinpoint the failing rule; rendered as prominent location chips rather than
// in the generic detail list below.
const LOCATION_KEYS = ['nodeId', 'procedure', 'ruleIndex', 'source'];

// Labels for the remaining, code-specific diagnostic fields.
const FIELD_LABELS: Record<string, { id: string; defaultMessage: string }> = {
    key: { id: 'cpq.pricing-trace.error.field.key', defaultMessage: 'Price key' },
    modelId: { id: 'cpq.pricing-trace.error.field.modelId', defaultMessage: 'Model' },
    bucket: { id: 'cpq.pricing-trace.error.field.bucket', defaultMessage: 'Bucket' },
    keyCurrency: {
        id: 'cpq.pricing-trace.error.field.keyCurrency',
        defaultMessage: 'Key currency',
    },
    lineCurrency: {
        id: 'cpq.pricing-trace.error.field.lineCurrency',
        defaultMessage: 'Line currency',
    },
    factor: { id: 'cpq.pricing-trace.error.field.factor', defaultMessage: 'Factor expression' },
    value: { id: 'cpq.pricing-trace.error.field.value', defaultMessage: 'Evaluated value' },
};

interface Location {
    nodeId?: string;
    procedure?: string;
    ruleIndex?: number;
}

// The failing rule can be named either through the flattened procedure/ruleIndex/nodeId or through
// the `source` triple, depending on the error; prefer the flattened fields, fall back to `source`.
function resolveLocation(diagnostics?: ExplainErrorDiagnostics): Location {
    if (!diagnostics) return {};
    return {
        nodeId: diagnostics.nodeId ?? diagnostics.source?.nodeId,
        procedure: diagnostics.procedure ?? diagnostics.source?.procedure,
        ruleIndex: diagnostics.ruleIndex ?? diagnostics.source?.ruleIndex,
    };
}

function formatValue(value: unknown): string {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
}

export function PricingErrorPanel({ error }: { error: ExplainError }) {
    const { formatMessage } = useIntl();

    // Guard failures (auth, disabled tenant) carry no code; show the flat message alone.
    if (!error.code) {
        return <Alert severity="error">{error.message}</Alert>;
    }

    const meaning = CODE_MEANINGS[error.code];
    const location = resolveLocation(error.diagnostics);
    const detailEntries = Object.entries(error.diagnostics ?? {}).filter(
        ([field, value]) => !LOCATION_KEYS.includes(field) && value !== undefined,
    );

    return (
        <Alert severity="error">
            <Stack gap={1}>
                <Typography variant="subtitle2">{error.code}</Typography>
                <Typography variant="body2">{error.message}</Typography>

                {meaning && (
                    <Typography variant="body2" color="textSecondary">
                        {formatMessage(meaning)}
                    </Typography>
                )}

                {(location.procedure || location.nodeId || location.ruleIndex !== undefined) && (
                    <Box>
                        <Typography variant="caption" color="textSecondary">
                            {formatMessage({
                                id: 'cpq.pricing-trace.error.location',
                                defaultMessage: 'Failing rule',
                            })}
                        </Typography>
                        <Stack direction="row" gap={1} flexWrap="wrap" marginTop={0.5}>
                            {location.nodeId && (
                                <Chip
                                    size="small"
                                    label={`${formatMessage({
                                        id: 'cpq.pricing-trace.error.field.node',
                                        defaultMessage: 'Node',
                                    })}: ${location.nodeId}`}
                                />
                            )}
                            {location.procedure && (
                                <Chip
                                    size="small"
                                    label={`${formatMessage({
                                        id: 'cpq.pricing-trace.error.field.procedure',
                                        defaultMessage: 'Procedure',
                                    })}: ${location.procedure}`}
                                />
                            )}
                            {location.ruleIndex !== undefined && (
                                <Chip
                                    size="small"
                                    label={`${formatMessage({
                                        id: 'cpq.pricing-trace.error.field.rule',
                                        defaultMessage: 'Rule',
                                    })}: #${location.ruleIndex}`}
                                />
                            )}
                        </Stack>
                    </Box>
                )}

                {detailEntries.length > 0 && (
                    <Box>
                        <Typography variant="caption" color="textSecondary">
                            {formatMessage({
                                id: 'cpq.pricing-trace.error.diagnostics',
                                defaultMessage: 'Diagnostics',
                            })}
                        </Typography>
                        <Stack gap={0.5} marginTop={0.5}>
                            {detailEntries.map(([field, value]) => {
                                const label = FIELD_LABELS[field];
                                return (
                                    <Stack key={field} direction="row" gap={1}>
                                        <Typography variant="body2" color="textSecondary">
                                            {`${label ? formatMessage(label) : field}:`}
                                        </Typography>
                                        <Typography variant="body2">
                                            {formatValue(value)}
                                        </Typography>
                                    </Stack>
                                );
                            })}
                        </Stack>
                    </Box>
                )}
            </Stack>
        </Alert>
    );
}
